import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { JobsListResponse } from './contractsMirror.js';
import type { JobsSearch } from './searchParams.js';

/**
 * TanStack Query hook for the COARSE jobs endpoint (the strangler-fig + coarse-endpoint proof,
 * CARVE-07). One query key per `{status, show_archived}` — toggling either drives exactly one
 * refetch of `GET /api/v1/jobs?status=…&show_archived=…`. The SERVER computes the job list (one
 * GROUP BY incl. the labor/hours SUMs), the per-job rollups, and the status counts (22-10); the
 * SPA only renders the returned `JobsListResponse` (no client re-aggregation, no new N+1 —
 * threat T-22-tenant / CARVE-07).
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived server-side
 * from the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant).
 *
 * NOTE: this hook also serves the invoice/estimate job picker (it previously lived as a stub in
 * `features/invoices/useJobsQuery.ts`); pass `enabled` to gate it when used as a picker.
 */

/** Build the stable query key for a filter (one cache entry per status + archived state). */
export function jobsQueryKey(search: JobsSearch): readonly unknown[] {
  return ['jobs', search.status ?? null, search.show_archived ?? false] as const;
}

/** Build the coarse-endpoint request path with the active filters (no tenant param). */
export function jobsRequestPath(search: JobsSearch): string {
  const params = new URLSearchParams();
  if (search.status) params.set('status', search.status);
  if (search.show_archived) params.set('show_archived', '1');
  const qs = params.toString();
  return qs ? `/api/v1/jobs?${qs}` : '/api/v1/jobs';
}

export function useJobsQuery(search: JobsSearch = {}, enabled = true) {
  return useQuery<JobsListResponse>({
    queryKey: jobsQueryKey(search),
    queryFn: () => apiFetchJson<JobsListResponse>(jobsRequestPath(search)),
    enabled,
  });
}
